import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import {
  Avatar,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useRef } from 'react'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { ColorTokens } from 'src/feature/theme/model/Palette'
import { formatAssistantText } from 'src/utils/formatAssistantText'
import { MessageRole } from '../model/ai-assistant.model'
import type {
  AiAssistantAction,
  AssistantMessage,
} from '../model/ai-assistant.model'

export type AssistantConversationLabels = {
  thinking: string;
  requestFailedTitle: string;
  retry: string;
  suggestedPrompts: string;
  actions: string;
  getActionLink: (label: string) => string;
};

export type AssistantConversationProps = {
  messages: AssistantMessage[];
  isTyping: boolean;
  errorMessage: string | null;
  suggestedPrompts: string[];
  actions: AiAssistantAction[];
  locale: string;
  avatar?: string;
  userName?: string;
  labels: AssistantConversationLabels;
  onRetry: () => void;
  onPromptClick: (prompt: string) => void;
  onActionLinkClick: (action: AiAssistantAction) => void;
};

const getUserInitials = (name?: string) => {
  if (!name) {
    return ''
  }

  const parts = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('')

  return parts || name.slice(0, 2).toUpperCase()
}

const formatMessageTimestamp = (timestamp: Date, locale: string) => new Intl.DateTimeFormat(locale, {
  dateStyle: 'short',
  timeStyle: 'short',
}).format(timestamp)

const renderAssistantMessage = (message: AssistantMessage) => {
  const lineOccurrences: Record<string, number> = {}

  return formatAssistantText(message.content).map((line) => {
    lineOccurrences[line] = (lineOccurrences[line] ?? 0) + 1

    return (
      <Typography
        key={`${message.id}-${line}-${lineOccurrences[line]}`}
        sx={styles.messageLine}
      >
        {line}
      </Typography>
    )
  })
}

export function AssistantConversation({
  messages,
  isTyping,
  errorMessage,
  suggestedPrompts,
  actions,
  locale,
  avatar,
  userName,
  labels,
  onRetry,
  onPromptClick,
  onActionLinkClick,
}: AssistantConversationProps) {
  const userInitials = getUserInitials(userName)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isTyping, suggestedPrompts, actions, errorMessage])

  return (
    <AppBox sx={styles.messages}>
      {messages.map(message => (
        <AppBox
          key={message.id}
          sx={
            message.role === MessageRole.User
              ? styles.userMessageRow
              : styles.assistantMessageRow
          }
        >
          {message.role === MessageRole.Assistant && (
            <Avatar sx={styles.assistantAvatar}>
              <AutoAwesomeRoundedIcon sx={{ fontSize: 14 }} />
            </Avatar>
          )}
          <AppBox sx={styles.bubbleColumn}>
            <AppBox
              sx={
                message.role === MessageRole.User
                  ? styles.userBubble
                  : styles.assistantBubble
              }
            >
              {message.role === MessageRole.Assistant ? (
                renderAssistantMessage(message)
              ) : (
                <Typography sx={styles.messageText}>
                  {message.content}
                </Typography>
              )}
            </AppBox>
            <Typography
              sx={
                message.role === MessageRole.User
                  ? styles.userTimestampText
                  : styles.assistantTimestampText
              }
            >
              {formatMessageTimestamp(message.timestamp, locale)}
            </Typography>
          </AppBox>
          {message.role === MessageRole.User && (
            <Avatar src={avatar} sx={styles.userAvatar}>
              {userInitials}
            </Avatar>
          )}
        </AppBox>
      ))}
      {isTyping && (
        <AppBox sx={styles.assistantMessageRow}>
          <Avatar sx={styles.assistantAvatar}>
            <AutoAwesomeRoundedIcon sx={{ fontSize: 14 }} />
          </Avatar>
          <AppBox sx={styles.typingIndicator}>
            <CircularProgress
              size={12}
              sx={{ color: ColorTokens.primary500 }}
            />
            <Typography sx={styles.typingText}>{labels.thinking}</Typography>
          </AppBox>
        </AppBox>
      )}
      {errorMessage && (
        <AppBox sx={styles.errorBox}>
          <Typography sx={styles.errorTitle}>
            {labels.requestFailedTitle}
          </Typography>
          <Typography sx={styles.errorText}>{errorMessage}</Typography>
          <AppButton
            fontSize={11}
            variant="light"
            sx={{ width: '10em' }}
            onClick={onRetry}
          >
            {labels.retry}
          </AppButton>
        </AppBox>
      )}
      {!isTyping && suggestedPrompts.length > 0 && (
        <AppBox sx={styles.sectionBlock}>
          <Typography sx={styles.sectionTitle}>
            {labels.suggestedPrompts}
          </Typography>
          <AppBox sx={styles.promptList}>
            {suggestedPrompts.map(prompt => (
              <AppBox
                key={prompt}
                sx={styles.promptChip}
                onClick={() => onPromptClick(prompt)}
              >
                <Typography sx={styles.promptText}>{prompt}</Typography>
              </AppBox>
            ))}
          </AppBox>
        </AppBox>
      )}
      {!isTyping && actions.length > 0 && (
        <AppBox sx={styles.sectionBlock}>
          <Typography sx={styles.sectionTitle}>{labels.actions}</Typography>
          <AppBox sx={styles.actionList}>
            {actions.map(action => (
              <AppBox
                key={`${action.label}-${action.suggestion}`}
                sx={styles.actionCard}
              >
                <AppBox sx={styles.actionHeader}>
                  <Typography sx={styles.actionLabel}>
                    {action.label}
                  </Typography>
                  {action.linkTo && (
                    <Tooltip
                      title={labels.getActionLink(action.label)}
                      placement="top"
                    >
                      <IconButton
                        size="small"
                        aria-label={labels.getActionLink(action.label)}
                        sx={styles.actionLink}
                        onClick={(event) => {
                          event.stopPropagation()
                          action.linkTo && onActionLinkClick(action)
                        }}
                      >
                        <ArrowForwardIosRoundedIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </AppBox>
                <Typography sx={styles.actionSuggestion}>
                  {action.suggestion}
                </Typography>
              </AppBox>
            ))}
          </AppBox>
        </AppBox>
      )}
      <div ref={messagesEndRef} />
    </AppBox>
  )
}

const styles = {
  messages: {
    width: '100%',
    flexDirection: 'column',
    gap: 10,
    padding: '10px 15px',
    overflowY: 'auto',
    flex: 1,
  },
  userMessageRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    gap: 8,
  },
  assistantMessageRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 8 },
  bubbleColumn: { flexDirection: 'column', maxWidth: '78%' },
  userBubble: {
    backgroundColor: ColorTokens.yellow100,
    borderRadius: '10px 10px 0px 10px',
    padding: '5px 10px',
  },
  assistantBubble: {
    backgroundColor: ColorTokens.primary10,
    borderRadius: '10px 10px 10px 0px',
    padding: '5px 10px',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  messageText: {
    fontSize: 12,
    lineHeight: 1.5,
    color: 'inherit',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  messageLine: {
    fontSize: 12,
    lineHeight: 1.5,
    color: 'inherit',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    marginBottom: 0.75,
    '&:last-of-type': { marginBottom: 0 },
  },
  assistantTimestampText: {
    marginTop: 3,
    fontSize: 10,
    color: 'text.disabled',
  },
  userTimestampText: {
    marginTop: 3,
    fontSize: 10,
    color: 'text.disabled',
    alignSelf: 'flex-end',
  },
  userAvatar: {
    width: 28,
    height: 28,
    fontSize: 11,
    flexShrink: 0,
    backgroundColor: ColorTokens.primary500,
  },
  assistantAvatar: {
    width: 28,
    height: 28,
    flexShrink: 0,
    backgroundColor: ColorTokens.primary10,
    color: ColorTokens.primary500,
    border: '1px solid rgba(54,187,175,0.3)',
  },
  typingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: '10px 15px',
    backgroundColor: 'background.dark',
    borderRadius: '10px 10px 10px 0px',
  },
  typingText: { fontSize: 12, color: 'text.secondary' },
  errorBox: {
    flexDirection: 'column',
    gap: 5,
    padding: '10px 15px',
    borderRadius: '12px',
    backgroundColor: '#FFF4F4',
    border: '1px solid rgba(255, 82, 82, 0.2)',
  },
  errorTitle: { fontSize: 12, fontWeight: 600, color: 'error.main' },
  errorText: { fontSize: 11, color: 'text.secondary' },
  sectionBlock: { flexDirection: 'column', gap: 8 },
  sectionTitle: { fontSize: 11, fontWeight: 600, color: 'text.secondary' },
  promptList: { flexDirection: 'column', gap: 8, alignItems: 'flex-start' },
  promptChip: {
    padding: '5px 10px',
    borderRadius: '999px',
    backgroundColor: 'background.dark',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': { backgroundColor: ColorTokens.primary10 },
  },
  promptText: { fontSize: 11, color: 'text.secondary' },
  actionList: { flexDirection: 'column', gap: 8 },
  actionCard: {
    padding: '5px 10px',
    borderRadius: '10px',
    backgroundColor: 'background.dark',
    flexDirection: 'column',
  },
  actionHeader: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  actionLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: ColorTokens.primary500,
    lineHeight: 1.2,
  },
  actionSuggestion: {
    fontSize: 11,
    color: 'text.secondary',
    lineHeight: 1.4,
  },
  actionLink: {
    width: 23,
    height: 23,
    flexShrink: 0,
    backgroundColor: 'background.paper',
    color: ColorTokens.primary500,
    '& .MuiSvgIcon-root': { fontSize: 13 },
  },
}
